import { useContext, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { FormSkeleton, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { VersionCreator } from "../../components/versions/VersionCreator";
import { Version } from "../../interfaces";
import { useVersions } from "../../hooks/useVersions";
import { AppContext } from "../../context/index";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { Column } from 'primereact/column';
import { Button, Calendar, InputText } from "iuvity-ui-components";
import { Dropdown } from 'primereact/dropdown';
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { MultiSelect } from 'primereact/multiselect'

const filterDefaultValues = {
  number: [],
  name: [],
  description: "",
  status: [],
  createdat: "",
  modifiedat: "",
};

export const AdminVersionPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, pageFilter, user },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          number: pageFilter?.number || [],
          name: pageFilter?.name || [],
          status: pageFilter?.status || "",
        }
      : filterDefaultValues,
  });

  const { control, setValue } = form;

  const { data, versionAction, reloadFn } = useVersions("DATA", false);

  const [modalProps, setModalProps] = useState<{
    visible: boolean;
    type: "CREATE" | "UPDATE" | null;
  }>({
    visible: false,
    type: null,
  });

  const [auxModalProps, setAuxModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const columns = useMemo(() => {
    return [
      {
        field: "number",
        header: t("Versions.numberLabel"),
      },
      {
        field: "name",
        header: t("Versions.nameLabel"),
      },
      {
        field: "description",
        header: t("Versions.descriptionLabel"),
      },
      {
        field: "status",
        header: t("Versions.statusLabel"),
      },
      {
        field: "createdat",
        header: t("Versions.createdatLabel"),
      },
      {
        field: "modifiedat",
        header: t("Versions.modifiedatLabel"),
      },
      {
        field: "actions",
        header: t("Versions.actionsLabel"),
      },
    ];
  }, [t]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    number: { value: pageFilter?.number, matchMode: FilterMatchMode.IN },
    name: { value: pageFilter?.name, matchMode: FilterMatchMode.IN },
    description: { value: pageFilter?.description, matchMode: FilterMatchMode.CONTAINS },
    status: { value: pageFilter?.status, matchMode: FilterMatchMode.EQUALS },
    createdat: { value: pageFilter?.createdat, matchMode: FilterMatchMode.CONTAINS },
    modifiedat: { value: pageFilter?.modifiedat, matchMode: FilterMatchMode.CONTAINS },
  } 

  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);

  const onShowModal = (type: "CREATE" | "UPDATE", version?: Version) => {
    if (version) {
      setAppState((prev) => ({
        ...prev,
        selectedVersion: version,
      }));
    }
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      type,
    }));
  };

  const onDeleteAction = async (version: Version) => {
    await versionAction("DELETE", version.id);
    reloadFn();
  };

  const handleModalProps = (version: Version) => {
    setAuxModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Versions.deleteModalTitle"),
      content: `${t("Versions.deleteModalContent1")} "${version.name}" ${t(
        "Versions.deleteModalContent2"
      )}`,
      onConfirm: () => onDeleteAction(version),
    }));
  };

  const onClearFilters = () => {
    // setFilteredData(data);
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  const formOptions = useMemo(() => {
    if (data && language) {
      return {
        number: data
          .map((version: any) => ({
            label: version?.number,
            value: version?.number,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
        name: data
          .map((version: any) => ({
            label: version?.name,
            value: version?.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
        status: ["active", "inactive"]
          .map((item: any) => ({
            label: t(`Versions.${item.toLowerCase()}StatusLabel`),
            value: item,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      };
    } else {
      return;
    }
  }, [t, data, language]);

  useEffect(() => {
    if (data) setFilteredData(data);
  }, [data]);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Versions.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Versions.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Versions.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.map((item: any) => (
        {
          label: item[`${options.field}`], 
          value: item[`${options.field}`]
        }))
      ?.sort((a: any, b: any) => a?.label?.localeCompare(b?.label));
    
    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Versions.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Versions.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("Translations.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const statuses = [
    `${t("Versions.activeStatusLabel")}`,
    `${t("Versions.inactiveStatusLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
            name={options.field}
            control={control}
            render={({ field }) => {
              field.onChange = (e) => {
                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                options.filterCallback(e.value);
              };
              return (
                <>
                  <label htmlFor={options.field} className="block lb mt-2 mb-3">
                    {t(`Versions.${options.field}Label`)}
                  </label>
                  {/* // @ts-ignore */}
                  <Dropdown 
                    options={statuses}
                    itemTemplate={statusItemTemplate}
                    placeholder={t(`Versions.${options.field}PlaceholderLabel`)}
                    className="p-column-filter mb-2"
                    showClear={false}
                    {...field} 
                    />
                </>
              );
            }}
          />
      
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Agents.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFilters}
      />
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Agents.applyFilterLabel")}
        className="p-button"
        // disabled={
        //   !(
        //     nameValues.length > 0 ||
        //     versionValues.length > 0 ||
        //     urlValues.length > 0 ||
        //     statusValues.length > 0
        //     )
        // }
        onClick={options.filterApplyCallback}
      />
    )
  };

  const formatDate = (value: any) => {
    return new Date(value).toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
  };

  const dateFilterTemplate = (options: any) => {
    return (
        <Calendar
          label={t(`Versions.${options.field}Label`)}
          value={options.value}
          onChange={(e) => options.filterCallback(e.value, options.index)}
          dateFormat="mm/dd/yy"
          placeholder="mm/dd/yyyy"
          mask="99/99/9999"
          className="mt-2 mb-2"
        />
      )
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("Versions.versionAdminTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Versions.versionsBreadcrumbLabel")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Versions.advancedOptionsBreadcrumbLabel"),
              style: { fontWeight: "bold" },
              disabled: true
            },
            {
              label: t("Versions.versionsBreadcrumbLabel"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Versions.versionAdminDescription1")}</span>
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        actions={[
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Versions.newVersionBtnLabel"),
            onClick: () => onShowModal("CREATE"),
          },
        ]}
      />

      <BoxFull className="dt-c my-4">
        {filteredData && formOptions ? (
          <DataTable
            header={
              <TableHeader
                title={t("Versions.versionsBreadcrumbLabel")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFilters}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            filters={filters}
            sortField="id"
            dataKey="id"
            sortOrder={1}
            value={filteredData.map((version: any) => ({
              ...version,
              status: t(`Versions.${version.status.toLowerCase()}StatusLabel`),
              createdat: new Date(formatDate(version.createdat)),
              modifiedat: new Date(formatDate(version.modifiedat)),
              actions: (
                <Actions
                  enableDelete={!!version.createdby}
                  onEdit={() => onShowModal("UPDATE", version)}
                  onDelete={() => handleModalProps(version)}
                />
              ),
            }))}
            scrollable
            resizableColumns
            globalFilterFields={['number', 'name', 'description', 'status', 'createdat', 'modifiedat']}
            filterDisplay="menu"
            stateStorage="session"
            stateKey="filters-table"
          >
            {selectedColumns.map((col) => {
              return (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions" ? "center" : "left"}
                  headerStyle={col.field === "actions"
                  ? { justifyContent: "center" }
                  : undefined }
                  style={{
                    boxShadow: (col.field === "actions")
                      ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                      : undefined,
                    maxWidth: (col.field === "actions") ? "132px" : undefined,
                    whiteSpace: "normal",
                    minWidth: (col.field === "actions") ? "8.3rem" : "14rem",
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: '23rem'}}
                  filter={col.field === "actions" ? false : true}
                  filterElement={
                    ((col.field === "number" || col.field === "name") && multiselectFilterTemplate) ||
                    ((col.field === "description") && filterTemplate) ||
                    (col.field === "status" && statusFilterTemplate) ||
                    ((col.field === "createdat" || col.field === "modifiedat") && dateFilterTemplate)
                  }
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  dataType={(col.field === "createdat" || col.field === "modifiedat") ? "date" : ""}
                  body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby', formatDate)}
                />
              );
            })}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      {modalProps.visible && (
        <VersionCreator
          visible={modalProps.visible}
          setModalProps={setModalProps}
          update={modalProps.type === "UPDATE"}
          createFn={versionAction}
          reloadFn={reloadFn}
          auxModalController={setAuxModalProps}
        />
      )}

      <CustomDialog
        title={auxModalProps.title}
        visible={auxModalProps.visible}
        content={auxModalProps.content}
        handleModalState={setAuxModalProps}
        onConfirm={auxModalProps.onConfirm}
      />
    </>
  );
};
